import React, {
    type CSSProperties,
    type MutableRefObject,
    type ReactNode,
    useEffect,
    useRef
} from 'react';

import {
    useFloating,
    useDismiss,
    useRole,
    useClick,
    useInteractions,
    useId,
    FloatingFocusManager,
    FloatingOverlay,
    FloatingPortal,
    useTransitionStatus
} from '@floating-ui/react';
import classnames from 'classnames';

import { type ButtonProps } from '../Button';
import { H3 } from '../Typography';

import { FooterActions, type FooterButtonProps } from './FooterActions';
import { MODAL_DEFAULT_WIDTH, MODAL_TRANSITION_DURATION } from './Modal.constants';

import styles from './Modal.module.scss';

type ModalRootProps = {
    children: React.ReactNode;
    isMounted: boolean;
} & Pick<ModalProps, 'destroyOnHide' | 'getContainer' | 'open'>;

const ModalRoot = ({ children, destroyOnHide, getContainer, isMounted, open }: ModalRootProps) => {
    const hasBeenOpened = useRef(false);

    if (open) {
        hasBeenOpened.current = true;
    }

    if (!isMounted && destroyOnHide) return null;

    if (!isMounted && !hasBeenOpened.current) return null;

    if (getContainer === false) {
        return children;
    }

    const resolvedContainer = typeof getContainer === 'function' ? getContainer() : getContainer;

    return <FloatingPortal root={resolvedContainer}>{children}</FloatingPortal>;
};

export type ModalProps = {
    cancelButtonProps?: Partial<FooterButtonProps>;
    cancelText?: string;
    centered?: boolean;
    children?: ReactNode;
    className?: string;
    destroyOnHide?: boolean;
    footer?: ReactNode;
    getContainer?: HTMLElement | (() => HTMLElement) | false;
    hideFooter?: boolean;
    isDestructive?: boolean;
    isWarning?: boolean;
    maskStyle?: CSSProperties;
    /**
     * minimum width of the modal
     */
    minWidth?: number | string;
    noPadding?: boolean;
    okButtonProps?: Partial<FooterButtonProps>;
    okText?: string;
    open?: boolean;
    title?: ReactNode;
    /**
     * initial width of the modal, it will scale down to fit
     * the screen until it reaches the `minWidth`
     */
    width?: number | string;
    afterClose?: () => void;
    onCancel?: () => void;
    onOk?: () => void;
    /**
     * Which element to initially focus. Can be either a number (tabbable index as
     * specified by the `order`) or a ref.
     */
    initialFocus?: number | MutableRefObject<HTMLElement | null>;
} & { [key: `data-${string}`]: string | undefined };

export const Modal = ({
    afterClose,
    cancelButtonProps,
    cancelText,
    centered,
    children,
    className,
    destroyOnHide,
    footer: footerProp,
    getContainer,
    hideFooter,
    isDestructive,
    isWarning,
    maskStyle,
    minWidth = MODAL_DEFAULT_WIDTH,
    noPadding,
    okButtonProps,
    okText,
    onCancel,
    onOk,
    open,
    title,
    width = MODAL_DEFAULT_WIDTH,
    initialFocus = 0,
    ...rest
}: ModalProps) => {
    const wasOpenRef = useRef(open);

    const { refs, context } = useFloating({
        open,
        onOpenChange: (isOpen, _, reason) => {
            if (!isOpen && reason === 'escape-key') {
                onCancel?.();
            }
        }
    });

    const { isMounted, status } = useTransitionStatus(context, {
        duration: MODAL_TRANSITION_DURATION
    });

    const click = useClick(context);
    const role = useRole(context);
    const dismiss = useDismiss(context, { outsidePress: false, escapeKey: true });

    const { getFloatingProps } = useInteractions([click, role, dismiss]);

    const headingId = useId();
    const descriptionId = useId();

    const getOkButtonAppearance = (): ButtonProps => {
        if (isDestructive) {
            return {
                color: 'alert'
            };
        }
        if (isWarning) {
            return {
                color: 'warning'
            };
        }
        return {
            color: 'primary'
        };
    };

    const getFooterButtons = () => {
        const buttons: FooterButtonProps[] = [];
        if (cancelText) {
            buttons.push({
                content: cancelText,
                color: 'neutral',
                type: 'outlined',
                onClick: onCancel,
                key: 'cancel',
                ...cancelButtonProps
            });
        }
        if (okText) {
            buttons.push({
                content: okText,
                onClick: onOk,
                ...getOkButtonAppearance(),
                key: 'ok',
                ...okButtonProps
            });
        }
        return buttons;
    };

    const getFooter = () => {
        if (hideFooter) {
            return null;
        }
        if (footerProp) {
            return footerProp;
        }
        const footerActions = getFooterButtons();
        if (footerActions.length > 0) {
            return <FooterActions buttons={getFooterButtons()} />;
        }
        return null;
    };

    const footer = getFooter();

    useEffect(() => {
        if (wasOpenRef.current && !open) {
            afterClose?.();
        }
        wasOpenRef.current = open;
    }, [open, afterClose]);

    return (
        <ModalRoot
            getContainer={getContainer}
            destroyOnHide={destroyOnHide}
            open={open}
            isMounted={isMounted}
        >
            <FloatingOverlay
                ref={refs.setFloating}
                className={classnames(styles.overlay, styles[`overlay-${status}`], {
                    [styles.centered]: centered
                })}
                style={
                    {
                        ...maskStyle,
                        ...(!isMounted &&
                            !destroyOnHide && {
                                display: 'none',
                                pointerEvents: 'none'
                            }),
                        '--modal-transition-duration': `${MODAL_TRANSITION_DURATION}ms`
                    } as CSSProperties
                }
                lockScroll
            >
                <FloatingFocusManager
                    context={context}
                    disabled={!open}
                    initialFocus={initialFocus}
                >
                    <div
                        className={classnames(
                            styles.modal,
                            {
                                [styles.noHeader]: !title,
                                [styles.centered]: centered
                            },
                            styles[`modal-${status}`],
                            className
                        )}
                        aria-labelledby={headingId}
                        aria-describedby={descriptionId}
                        aria-modal
                        style={{ minWidth, width }}
                        {...getFloatingProps()}
                        {...rest}
                    >
                        <div
                            className={classnames(styles.content, {
                                [styles.noPadding]: noPadding
                            })}
                        >
                            {title && (
                                <div className={styles.header}>
                                    <H3 id={headingId} truncate className={styles.title}>
                                        {title}
                                    </H3>
                                </div>
                            )}
                            <div className={styles.body}>{children}</div>
                            {footer && <div className={styles.footer}>{footer}</div>}
                        </div>
                    </div>
                </FloatingFocusManager>
            </FloatingOverlay>
        </ModalRoot>
    );
};
